// Generates an on-brand PDF of a user's IR-Score "Founder Diagnostic Report".
// Distinct from the Capital Readiness PDF: the cover is profile-led (the founder
// archetype is the hero) with a horizontal R1→R7 "you are here" journey, and the
// body renders the diagnostic sections. Consulting blue (#1976D2) → navy palette,
// Montserrat typeface. pdfmake + fonts are imported dynamically (kept out of the bundle).
//
// Reports open in a NEW TAB (view online + download) rather than force-downloading.

import type { IrReport, IrBlock, IrSection, Tone } from '@/data/irReports';
import { IR_STAGES } from '@/data/irReports';
import { LOGO_WHITE_DATAURL } from '@/utils/brandLogo';

interface IrReportPdfOptions {
  userName: string;
  score: number;
  dateLabel: string;
}

// ── Brand palette ───────────────────────────────────────────────────────────
const BRAND = '#1976D2';
const NAVY = '#17263A';
const BODY = '#42505F';
const MUTED = '#6E7888';
const FAINT = '#9AA4B2';
const LINE = '#E5E8EA';
const CARD = '#F6F9FD';
const BLUE_TINT = '#EAF2FB';

const STATUS: Record<Tone, string> = {
  red: '#C0392B', orange: '#E0592A', yellow: '#E08A1E', blue: '#1976D2', green: '#15824E', neutral: '#6E7888',
};
const STATUS_TINT: Record<Tone, string> = {
  red: '#FBEAE7', orange: '#FCEDE6', yellow: '#FBF1DF', blue: '#EAF2FB', green: '#E7F4EC', neutral: '#EEF2F7',
};
const toneColor = (t?: Tone) => STATUS[t ?? 'neutral'];

const CONTENT_WIDTH = 515;
const PAGE_W = 595.28;
const PAGE_H = 841.89;

function escapeXml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// ── SVG builders ─────────────────────────────────────────────────────────────
function coverBackgroundSvg(): string {
  return `<svg width="${PAGE_W}" height="${PAGE_H}" viewBox="0 0 ${PAGE_W} ${PAGE_H}" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="bg" x1="0" y1="0" x2="0.35" y2="1">
      <stop offset="0" stop-color="#1E63B8"/>
      <stop offset="0.5" stop-color="${BRAND}"/>
      <stop offset="1" stop-color="${NAVY}"/>
    </linearGradient>
    <radialGradient id="glow" cx="0.15" cy="0.12" r="0.8">
      <stop offset="0" stop-color="#8FC0F5" stop-opacity="0.35"/>
      <stop offset="1" stop-color="#8FC0F5" stop-opacity="0"/>
    </radialGradient>
  </defs>
  <rect width="${PAGE_W}" height="${PAGE_H}" fill="url(#bg)"/>
  <rect width="${PAGE_W}" height="${PAGE_H}" fill="url(#glow)"/>
  <circle cx="60" cy="120" r="220" fill="none" stroke="#FFFFFF" stroke-opacity="0.06" stroke-width="1.5"/>
  <circle cx="60" cy="120" r="150" fill="none" stroke="#FFFFFF" stroke-opacity="0.05" stroke-width="1.5"/>
  <circle cx="560" cy="770" r="180" fill="none" stroke="#FFFFFF" stroke-opacity="0.05" stroke-width="1.5"/>
</svg>`;
}

// Horizontal R1→R7 journey, highlighting the current stage. Rendered light for the dark cover.
function journeySvg(currentIndex: number, stageColor: string): { svg: string; width: number } {
  const W = CONTENT_WIDTH, H = 62, padX = 18, y = 34;
  const n = IR_STAGES.length;
  const step = (W - 2 * padX) / (n - 1);
  const xAt = (i: number) => padX + i * step;
  const cur = Math.max(0, Math.min(n - 1, currentIndex));

  let s = `<svg width="${W}" height="${H}" viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg">`;
  // base track
  s += `<line x1="${xAt(0)}" y1="${y}" x2="${xAt(n - 1)}" y2="${y}" stroke="#FFFFFF" stroke-opacity="0.22" stroke-width="3" stroke-linecap="round"/>`;
  // progress track
  s += `<line x1="${xAt(0)}" y1="${y}" x2="${xAt(cur)}" y2="${y}" stroke="${stageColor}" stroke-width="3" stroke-linecap="round"/>`;
  // nodes + labels
  for (let i = 0; i < n; i++) {
    const isCur = i === cur;
    const done = i <= cur;
    const r = isCur ? 9 : 5.5;
    const cx = xAt(i);
    if (isCur) {
      s += `<circle cx="${cx}" cy="${y}" r="12" fill="${stageColor}" fill-opacity="0.25"/>`;
      s += `<circle cx="${cx}" cy="${y}" r="${r}" fill="#FFFFFF" stroke="${stageColor}" stroke-width="3"/>`;
    } else {
      s += `<circle cx="${cx}" cy="${y}" r="${r}" fill="#FFFFFF" fill-opacity="${done ? '0.95' : '0.35'}"/>`;
    }
    const labelColor = isCur ? '#FFFFFF' : '#C6D7EC';
    const weight = isCur ? 'bold' : 'normal';
    s += `<text x="${cx}" y="${y + 22}" text-anchor="middle" font-family="Montserrat" font-size="8.5" font-weight="${weight}" fill="${labelColor}">R${i + 1}</text>`;
  }
  // "you are here" marker above current
  const pillW = 66, pillX = Math.max(2, Math.min(W - pillW - 2, xAt(cur) - pillW / 2));
  s += `<rect x="${pillX}" y="2" width="${pillW}" height="14" rx="7" fill="#FFFFFF"/>`;
  s += `<text x="${pillX + pillW / 2}" y="11.5" text-anchor="middle" font-family="Montserrat" font-size="7" font-weight="bold" fill="${NAVY}">YOU ARE HERE</text>`;
  s += `</svg>`;
  return { svg: s, width: W };
}

function badgeSvg(n: number, color: string): string {
  return `<svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><circle cx="10" cy="10" r="10" fill="${color}"/><text x="10" y="13.6" text-anchor="middle" font-family="Montserrat" font-size="10.5" font-weight="bold" fill="#FFFFFF">${n}</text></svg>`;
}

// A pill for the pillar · stage line on the cover.
function stageBadgeSvg(pillar: string, stage: string, stageColor: string): { svg: string; width: number } {
  const label = escapeXml(`${pillar.toUpperCase()}  ·  ${stage.toUpperCase()}`);
  const w = Math.min(CONTENT_WIDTH, 26 + label.length * 5.2);
  const svg = `<svg width="${w}" height="24" viewBox="0 0 ${w} 24" xmlns="http://www.w3.org/2000/svg">
  <rect x="0" y="0" width="${w}" height="24" rx="12" fill="#FFFFFF" fill-opacity="0.12"/>
  <circle cx="14" cy="12" r="4" fill="${stageColor}"/>
  <text x="26" y="15.5" font-family="Montserrat" font-size="8.5" font-weight="bold" letter-spacing="0.8" fill="#FFFFFF">${label}</text>
</svg>`;
  return { svg, width: w };
}

// ── Block → pdfmake content ───────────────────────────────────────────────────
function renderBlock(block: IrBlock, accent: string): any[] {
  switch (block.kind) {
    case 'paragraph':
      return [{ text: block.text, style: 'p', margin: [0, 0, 0, 7] }];
    case 'bullets':
      // Clone the array — pdfmake mutates `ul` items in place during layout, which
      // would otherwise corrupt the shared source data (React then renders objects).
      return [{ ul: [...block.items], style: 'p', margin: [4, 1, 0, 9], markerColor: accent }];
    case 'callout':
      return [{
        table: {
          widths: [3.5, '*'],
          body: [[
            { text: '', fillColor: block.tone ? toneColor(block.tone) : BRAND },
            { text: block.text, fillColor: block.tone ? STATUS_TINT[block.tone] : BLUE_TINT, color: NAVY, bold: true, margin: [12, 9, 12, 9], lineHeight: 1.25 },
          ]],
        },
        layout: 'noBorders',
        margin: [0, 3, 0, 10],
      }];
    case 'steps':
      return block.items.map((item, i) => ({
        margin: [0, 2, 0, 9],
        stack: [
          {
            columns: [
              { width: 20, svg: badgeSvg(i + 1, accent) },
              { width: '*', text: item.title, bold: true, color: NAVY, fontSize: 11, margin: [0, 2, 0, 0] },
            ],
            columnGap: 8,
          },
          ...(item.sub && item.sub.length
            ? [{ ul: [...item.sub], margin: [26, 5, 0, 0], style: 'p', markerColor: accent }]
            : []),
        ],
      }));
    default:
      return [];
  }
}

function sectionHeader(index: number, title: string): any {
  return {
    stack: [
      {
        columns: [
          { width: 20, svg: badgeSvg(index, BRAND) },
          { width: '*', text: title, style: 'h2', margin: [0, 2, 0, 0] },
        ],
        columnGap: 8,
      },
      { canvas: [{ type: 'line', x1: 0, y1: 0, x2: CONTENT_WIDTH, y2: 0, lineWidth: 1, lineColor: LINE }], margin: [0, 6, 0, 9] },
    ],
  };
}

function renderSection(section: IrSection, index: number): any[] {
  const body: any[] = [];
  for (const b of section.blocks) body.push(...renderBlock(b, BRAND));
  return [{ stack: [sectionHeader(index, section.title), ...body], unbreakable: true, margin: [0, 14, 0, 0] }];
}

// ── Document assembly ──────────────────────────────────────────────────────────
export function buildIrDocDefinition(report: IrReport, opts: IrReportPdfOptions): any {
  const stageColor = toneColor(report.tone);
  const currentIndex = IR_STAGES.findIndex((s) => s.rCode === report.rCode);
  const journey = journeySvg(currentIndex < 0 ? 0 : currentIndex, stageColor);
  const badge = stageBadgeSvg(report.pillar, report.stage, stageColor);

  const cover: any[] = [
    { image: LOGO_WHITE_DATAURL, width: 130, margin: [0, 2, 0, 0] },
    { text: 'FOUNDER DIAGNOSTIC REPORT', font: 'MontserratSemi', color: '#BFD9F5', characterSpacing: 2.6, fontSize: 9, margin: [0, 40, 0, 0] },
    { text: report.profile, color: '#FFFFFF', bold: true, fontSize: 34, lineHeight: 1.05, margin: [0, 8, 40, 0] },
    { svg: badge.svg, width: badge.width, margin: [0, 14, 0, 0] },
    { text: report.tagline, color: '#DCEAFB', fontSize: 11.5, lineHeight: 1.45, margin: [0, 16, 70, 0] },
    { text: 'YOUR READINESS JOURNEY', font: 'MontserratSemi', color: '#9FB6D4', characterSpacing: 1.8, fontSize: 7.5, margin: [0, 30, 0, 0] },
    { svg: journey.svg, width: journey.width, margin: [0, 8, 0, 0] },
    {
      text: [
        { text: 'Prepared for ', color: '#9FB6D4' },
        { text: opts.userName, color: '#FFFFFF', bold: true },
        { text: `   ·   IR Score ${opts.score}/100   ·   ${opts.dateLabel}`, color: '#9FB6D4' },
      ],
      absolutePosition: { x: 40, y: 802 }, fontSize: 9.5,
    },
    { text: '', pageBreak: 'after' },
  ];

  const sectionContent = report.sections.flatMap((section, i) => renderSection(section, i + 1));

  return {
    pageSize: 'A4',
    pageMargins: [40, 44, 40, 52],
    info: { title: `Founder Diagnostic Report: ${report.profile} (${report.key})`, author: 'Conncct', subject: `${report.pillar} · ${report.stage}` },
    background: (currentPage: number) =>
      currentPage === 1
        ? { svg: coverBackgroundSvg(), width: PAGE_W, height: PAGE_H }
        : { canvas: [{ type: 'rect', x: 0, y: 0, w: PAGE_W, h: 5, color: BRAND }] },
    footer: (currentPage: number, pageCount: number) => {
      if (currentPage === 1) return undefined;
      return {
        margin: [40, 8, 40, 0],
        stack: [
          { canvas: [{ type: 'line', x1: 0, y1: 0, x2: CONTENT_WIDTH, y2: 0, lineWidth: 0.8, lineColor: LINE }], margin: [0, 0, 0, 6] },
          {
            columns: [
              { text: 'CONNCCT  ·  CONFIDENTIAL', font: 'MontserratSemi', color: FAINT, fontSize: 7.5, characterSpacing: 0.6 },
              { text: `${report.pillar} · ${report.key}`, color: FAINT, fontSize: 7.5, alignment: 'center' },
              { text: `${currentPage} / ${pageCount}`, color: FAINT, fontSize: 7.5, alignment: 'right' },
            ],
          },
        ],
      };
    },
    content: [...cover, ...sectionContent],
    styles: {
      h2: { fontSize: 13.5, bold: true, color: NAVY },
      p: { fontSize: 10, color: BODY, lineHeight: 1.34 },
    },
    defaultStyle: { font: 'Montserrat', fontSize: 10, color: BODY, lineHeight: 1.34 },
  };
}

async function loadPdfMake(): Promise<any> {
  const pdfMakeMod: any = await import('pdfmake/build/pdfmake');
  const vfsMod: any = await import('pdfmake/build/vfs_fonts');
  const { MONTSERRAT_VFS } = await import('@/utils/montserratFont');
  const pdfMake = pdfMakeMod.default ?? pdfMakeMod;
  const robotoVfs = vfsMod.default ?? vfsMod;
  pdfMake.addVirtualFileSystem({ ...robotoVfs, ...MONTSERRAT_VFS });
  pdfMake.setFonts({
    Montserrat: { normal: 'Montserrat-Regular.ttf', bold: 'Montserrat-Bold.ttf', italics: 'Montserrat-Regular.ttf', bolditalics: 'Montserrat-Bold.ttf' },
    MontserratSemi: { normal: 'Montserrat-SemiBold.ttf', bold: 'Montserrat-Bold.ttf', italics: 'Montserrat-SemiBold.ttf', bolditalics: 'Montserrat-Bold.ttf' },
    Roboto: { normal: 'Roboto-Regular.ttf', bold: 'Roboto-Medium.ttf', italics: 'Roboto-Italic.ttf', bolditalics: 'Roboto-MediumItalic.ttf' },
  });
  return pdfMake;
}

// Open the report in a NEW TAB for online viewing (with the browser's built-in
// download control). Pass a pre-opened window (opened synchronously on the user's
// click) to avoid popup blockers; otherwise falls back to opening one here, and to
// a direct download if the popup is blocked entirely.
export async function openIrReportPdf(report: IrReport, opts: IrReportPdfOptions, targetWindow?: Window | null): Promise<void> {
  const pdfMake = await loadPdfMake();
  const fileName = `Founder-Diagnostic-Report-${report.key}.pdf`;
  // pdfmake 0.3.x: getBlob() returns a Promise<Blob> (no callback).
  const blob: Blob = await pdfMake.createPdf(buildIrDocDefinition(report, opts)).getBlob();
  const url = URL.createObjectURL(blob);
  const w = targetWindow && !targetWindow.closed ? targetWindow : window.open('', '_blank');
  if (w) {
    try { w.location.href = url; } catch { /* ignore */ }
  } else {
    const a = document.createElement('a');
    a.href = url; a.download = fileName;
    document.body.appendChild(a); a.click(); a.remove();
  }
  setTimeout(() => URL.revokeObjectURL(url), 120000);
}
